import React, { useContext, useEffect } from 'react';
import { Router } from 'react-router-dom';

import { ApolloProvider } from '@apollo/client/react';

import { createBrowserHistory } from 'history';
import { WithStyles } from '@material-ui/core/styles';

import { CssBaseline, withStyles } from '@material-ui/core';

import Routes from '../../Routes';
import AuthProvider, { AuthContext } from '../AuthProvider';
import StateProvider from '../StateProvider';
import ApolloClient from '../../utils/apolloClient';
import ErrorBoundary from '../ErrorBoundary';

import CustomThemeProvider from '../CustomThemeProvider';
import { ThemeModes } from '../CustomThemeProvider/theme';
import { SnackbarProvider } from 'notistack';
import { QueryClient, QueryClientProvider } from 'react-query';
import { logger } from '../../utils/logger';

import styles from './styles';

const browserHistory = createBrowserHistory();

interface Props extends WithStyles<typeof styles> {}

const modes: ThemeModes = {
  primary: {
    main: '#95C01F',
    light: '#95C01F70',
    contrastText: '#FFFFFF',
  },
  secondary: {
    main: '#EE8E01',
    light: '#EE8E0170',
    contrastText: '#FFFFFF',
  },
  light: {
    background: {
      paper: '#FFFFFF',
      default: '#f4f4f4',
    },
    text: {
      primary: '#000000DE',
      secondary: '#000000D2',
    },
  },
  dark: {
    background: {
      paper: '#222b36',
      default: '#171c24',
    },
    text: {
      primary: '#FFFFFF',
      secondary: '#FFFFFFB3',
    },
  },
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
});

/**
 * RouterWithLogging component that logs route changes with auth context
 * Must be inside AuthProvider to access userRole
 */
const RouterWithLogging = ({ classes }: { classes: any }): React.ReactElement => {
  const authContext = useContext(AuthContext);

  useEffect(() => {
    // Log initial route
    logger.info('App initialized', {
      route: window.location.pathname,
      userRole: authContext?.userRole || undefined,
      userId: authContext?.userId || undefined,
    });

    // Listen to history changes and log route transitions
    const unlisten = browserHistory.listen((location, action) => {
      const token = localStorage.getItem('token');
      const tokenExpiry = logger.getTokenExpiry(token);

      logger.logRouteChange(
        window.location.pathname,
        location.pathname,
        {
          userRole: authContext?.userRole || undefined,
          userId: authContext?.userId || undefined,
          tokenExpiry: tokenExpiry || undefined,
        }
      );
    });

    // Cleanup listener on unmount
    return () => {
      unlisten();
    };
  }, [authContext?.userRole, authContext?.userId]);

  return (
    <div className={classes.root}>
      <Router history={browserHistory}>
        <Routes />
      </Router>
    </div>
  );
};

const App = ({ classes }: Props): React.ReactElement => {
  return (
    <CustomThemeProvider modes={modes}>
      <QueryClientProvider client={queryClient}>
        <SnackbarProvider>
          <CssBaseline />
          <ErrorBoundary>
            <AuthProvider>
              <ApolloProvider client={ApolloClient}>
                <StateProvider>
                  <RouterWithLogging classes={classes} />
                </StateProvider>
              </ApolloProvider>
            </AuthProvider>
          </ErrorBoundary>
        </SnackbarProvider>
      </QueryClientProvider>
    </CustomThemeProvider>
  );
};

export default withStyles(styles)(App);
